/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  cloneElement,
} from 'react';
import { css, jsx } from '@emotion/core';
import { keyframes } from 'emotion';
import Arrow from 'components/UI/Arrow';
import { Box, Text, Flexbox, Row } from '@chameleoncreator/creator-ui';
// import posed from 'react-pose';

import { ThemeProvider } from 'emotion-theming';
import { interactionContext } from './interactionContext';
import isIE from 'services/isIE';
import isiOS from 'services/isiOS';
import isAppleComputerInc from 'services/isAppleComputerInc';
import isAndroidSamsung from 'services/isAndroidSamsung';
import getBlockById from 'services/apiHelpers/getBlockById';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import { motion } from 'framer-motion';

import IELayout from './IELayout';

const fader = keyframes`
from {
  opacity: 0;
}
to {
  opacity: 1;
}
`;

// Close all the accordions (used when e)
const close = (obj: {}): {} => {
  return Object.keys(obj).reduce((a, k) => {
    a[k] = false;
    return a;
  }, {});
};

// The useAccordion hook is a state object that allows
// for accordions open/close state to be tracked and altered.
// If the exclusive tag is passed in, it will close all the
// fields when one is openned
const useAccordion = (
  unlock: () => void,
  count: number,
  exclusive?: boolean
) => {
  const [state, setState] = useState({
    open: {},
    viewed: {},
  });

  const toggle = (key: string) => {
    const past = exclusive ? close(state.open) : { ...state.open };
    const viewed = state.viewed[key.toString()]
      ? state.viewed
      : { ...state.viewed, [key.toString()]: true };

    if (Object.keys(viewed).length === count) {
      unlock();
    }

    setState({
      ...state,
      viewed,
      open: { ...past, [key]: !state.open[key] },
    });
  };

  const isOpen = (key: string) => {
    return state.open[key] || false;
  };

  return { toggle, isOpen };
};

interface AccordionCtx {
  toggle(key: string): void;
  isOpen(key: string): boolean;
}

const AccordionContext = createContext<AccordionCtx>({
  toggle: () => {},
  isOpen: () => false,
});

// A field is the grouping of headers and bodies of accordion fields.
// It passes information down the the header / body to toggle and
// open status
export const Field = ({ children, ...props }) => {
  const { toggle, isOpen } = useContext(AccordionContext);
  const { headingColor: borderColor } = useContext(interactionContext).props;
  const idx = props.idx;
  const trigger = () => {
    toggle(idx);
  };
  const isFieldOpen = isOpen(idx);

  return (
    <Box
      mt={1}
      mb={1}
      border={`1px solid ${borderColor || '#222'}`}
      // aria-expanded={isFieldOpen}
    >
      {React.Children.map(children, child =>
        cloneElement(child, { trigger, isOpen: isFieldOpen })
      )}
    </Box>
  );
};

interface HeaderProps {
  children: JSX.Element | string;
  foreground?: string;
  background?: string;
}

interface InjectdProps {
  isOpen: boolean;
  trigger(key: string): void;
}

// The Field Header is
export const FieldHeader = ({
  children,
  foreground = '#fff',
  background = '#222',
  ...props
}: HeaderProps) => {
  const { isOpen, trigger } = props as InjectdProps;

  const { headingColor, headingTextColor } = useContext(
    interactionContext
  ).props;

  const spaceBarTrigger = useRef(null);

  return (
    <ThemeProvider theme={{ Text: { color: headingTextColor || foreground } }}>
      <Flexbox
        as="button"
        border="none"
        bg={headingColor || background}
        flexDirection="row"
        alignItems="center"
        css={css`
          &:focus {
            box-shadow: 0 0 0 1px #fff, 0 0 0 2px ${background};
          }
        `}
        style={{ width: '100%', cursor: 'pointer' }}
        onClick={e => {
          if (spaceBarTrigger.current) return (spaceBarTrigger.current = false);
          trigger('');
        }}
        onKeyDown={e => {
          if (e.keyCode === 32) {
            spaceBarTrigger.current = true;
          }
        }}
        p={2}
        aria-expanded={isOpen}
      >
        <Text
          as="div"
          flex="1"
          textAlign="left"
          css={css`
            * div {
              color: ${headingTextColor};
            }
          `}
        >
          {children}
        </Text>
        <Box flex={0} p={1} pl={3} pr={2}>
          <Arrow
            color={headingTextColor || '#fff'}
            rotate={isOpen ? 90 : 0}
            delay={isOpen ? 0.25 : 0}
          />
        </Box>
      </Flexbox>
    </ThemeProvider>
  );
};

// This posed element is responsible for the transition between open and closed states
// const Collapse = posed.div({
//   closed: { height: 0 },
//   open: { height: 'auto' },
// });

export const FieldBody = ({ children, ...props }) => {
  const { isOpen } = props as InjectdProps;

  const { id } = props;

  const fieldBody = useRef(null);

  const { fieldBodyBackgroundColor, fieldBodyTextColor } = useContext(
    interactionContext
  ).props;

  //
  // var it for testing . . .
  const isDevice = isAppleComputerInc() || isAndroidSamsung();
  if (isDevice) {
    return (
      <Row
        key={Math.random()}
        ref={fieldBody}
        id={`i${id}`}
        className={isOpen ? 'fader_1s' : ''}
        css={css`
          height: ${isOpen ? 'auto' : 0};
          margin: 0 !important;
          padding: 0 !important;
          background: ${fieldBodyBackgroundColor};

          * {
            color: ${fieldBodyTextColor} !important;
          }

          overflow: hidden;
        `}
        aria-hidden={!isOpen}
        aria-live="assertive"
      >
        {children}
      </Row>
    );
  }

  return (
    <motion.div
      ref={fieldBody}
      id={`i${id}`}
      style={{
        // visibility: 'hidden',
        display: 'none',
        overflow: 'hidden',
        background: fieldBodyBackgroundColor,
      }}
      initial={{ height: 0 }}
      transition={{
        duration: 0.225,
        ease: 'easeOut',
      }}
      animate={{ height: isOpen ? 'auto' : 0 }}
      onAnimationStart={() => {
        if (isOpen && fieldBody.current) {
          // fieldBody.current.style.visibility = 'visible';
          fieldBody.current.style.display = '';
        }
      }}
      onAnimationComplete={() => {
        if (!isOpen && fieldBody.current) {
          // fieldBody.current.style.visibility = 'hidden';
          fieldBody.current.style.display = 'none';
        }
      }}
      aria-hidden={!isOpen}
      aria-live="assertive"
    >
      <Row
        css={css`
          * {
            color: ${fieldBodyTextColor} !important;
          }
        `}
      >
        {children}
      </Row>
    </motion.div>
  );
};

// An accordion is made up of 1 or more children
interface AccordionProps {
  children: JSX.Element[] | JSX.Element;
}

export const Accordion = ({ children }: AccordionProps) => {
  const iContext = useContext(interactionContext);

  const block = getBlockById(iContext.id);
  const unlock = () => {
    if (block.props.lockDown === 0) {
      return;
    }
    setBlockProps(block, { lockDown: 0 });
  };

  const ctx = useAccordion(unlock, React.Children.count(children), true);

  return (
    <AccordionContext.Provider value={{ ...ctx }}>
      {React.Children.map(children, (c, i) => cloneElement(c, { idx: i }))}
    </AccordionContext.Provider>
  );
};
